import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../api/axios';
import Layout from '../../common/components/Navbar';
import {
  Title,
  Group,
  Button,
  Paper,
  Table,
  Badge,
  ActionIcon,
  Tooltip,
  Modal,
  Select,
  TextInput,
  Textarea,
  Box,
  Text,
  ScrollArea,
  FileInput,
  Image,
  Stack,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import '@mantine/dates/styles.css';
import { useDisclosure } from '@mantine/hooks';
import { 
  IconPlus, 
  IconEye, 
  IconMessageReport,
  IconUpload,
  IconX,
  IconCalendar,
  IconMapPin,
  IconUsers
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

const ResidentTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [opened, { open, close }] = useDisclosure(false);
  
  const user = JSON.parse(localStorage.getItem('user'));

  // View Details
  const [viewOpened, { open: openView, close: closeView }] = useDisclosure(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Form Data (Log Ticket)
  const [formData, setFormData] = useState({
    type: 'complaint',
    subject: '',
    description: '',
    // Additional Fields
    incidentLocation: '',
    incidentDate: null,
    personsInvolved: '',
    serviceType: '',
    preferredDate: null
  });
  const [selectedFiles, setSelectedFiles] = useState([]);

  const fetchTickets = useCallback(async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${user.token}` } };
      const { data } = await axios.get('/api/complaints/my-complaints', config);
      setTickets(data);
    } catch (error) {
      console.error(error);
    }
  }, [user.token]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (files) => {
     if (files.length > 3) {
         notifications.show({ title: 'Error', message: "Max 3 images allowed", color: 'red' });
         return;
     }
     setSelectedFiles(files);
  };

  const handleSubmit = async () => {
    // --- 1. GENERAL VALIDATION ---
    if (!formData.subject.trim()) return notifications.show({ title: 'Validation Error', message: "Subject is required", color: 'red' });
    if (!formData.description.trim()) return notifications.show({ title: 'Validation Error', message: "Description is required", color: 'red' });

    // --- 2. TYPE-SPECIFIC VALIDATION ---
    if (formData.type === 'service_request') {
        if (!formData.serviceType) return notifications.show({ title: 'Validation Error', message: "Please select a Service Type", color: 'red' });
        if (!formData.preferredDate) return notifications.show({ title: 'Validation Error', message: "Please select a Preferred Date", color: 'red' });
    } 
    else if (formData.type === 'complaint' || formData.type === 'incident_report') {
        if (!formData.incidentLocation.trim()) return notifications.show({ title: 'Validation Error', message: "Incident Location is required", color: 'red' });
        if (!formData.incidentDate) return notifications.show({ title: 'Validation Error', message: "Incident Date is required", color: 'red' });
    }

    try {
      const config = { 
        headers: { 
            Authorization: `Bearer ${user.token}`,
            'Content-Type': 'multipart/form-data'
        } 
      };

      const payload = new FormData();
      payload.append('type', formData.type);
      payload.append('subject', formData.subject);
      payload.append('description', formData.description);
      
      // Append Conditional Fields (Safe Date Handling)
      if (formData.type === 'complaint' || formData.type === 'incident_report') {
          payload.append('incidentLocation', formData.incidentLocation);
          // FIX: Wrap in new Date() to ensure it is a valid Date object before calling toISOString
          if (formData.incidentDate) {
              payload.append('incidentDate', new Date(formData.incidentDate).toISOString());
          }
          payload.append('personsInvolved', formData.personsInvolved || '');
      } else if (formData.type === 'service_request') {
          payload.append('serviceType', formData.serviceType);
          // FIX: Wrap in new Date() to ensure it is a valid Date object before calling toISOString
          if (formData.preferredDate) {
              payload.append('preferredDate', new Date(formData.preferredDate).toISOString());
          }
      }

      selectedFiles.forEach(file => {
          payload.append('proofImages', file);
      });

      await axios.post('/api/complaints', payload, config);
      
      close();
      // Reset Form
      setFormData({ 
          type: 'complaint', subject: '', description: '',
          incidentLocation: '', incidentDate: null, personsInvolved: '',
          serviceType: '', preferredDate: null
      });
      setSelectedFiles([]);
      fetchTickets();
      notifications.show({ title: 'Success', message: "Ticket created successfully", color: 'green' });
    } catch (error) {
      console.error(error);
      notifications.show({ title: 'Error', message: error.response?.data?.message || "Failed to create ticket", color: 'red' });
    }
  };

  const handleViewDetails = (ticket) => {
    setSelectedTicket(ticket);
    openView();
  };

  const getStatusColor = (status) => {
    if (status === 'resolved') return 'green';
    if (status === 'rejected') return 'red';
    if (status === 'in_progress') return 'orange';
    return 'gray';
  };

  return (
    <Layout>
      <Group justify="space-between" mb="lg">
        <Title order={2} style={{ fontSize: '1.5rem' }}>My Complaints & Requests</Title>
        
        <Button 
          color="orange"
          leftSection={<IconPlus size={18} />} 
          onClick={open}
          size="sm"
        >
          Log New Ticket
        </Button>
      </Group>

      <Paper withBorder>
        <ScrollArea>
          <Table striped highlightOnHover minWidth={600}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Ticket ID</Table.Th> 
                <Table.Th>Date Filed</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Subject</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th style={{ textAlign: 'center' }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {tickets.map((ticket) => (
                <Table.Tr key={ticket._id}>
                  <Table.Td fw={700}>{ticket.ticketId || '-'}</Table.Td>
                  <Table.Td>{new Date(ticket.dateFiled).toLocaleDateString()}</Table.Td>
                  <Table.Td style={{ textTransform: 'capitalize' }}>{ticket.type.replace('_', ' ')}</Table.Td>
                  <Table.Td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ticket.subject}
                  </Table.Td>
                  <Table.Td>
                    <Badge 
                      color={getStatusColor(ticket.status)}
                    >
                      {ticket.status.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </Table.Td>
                  <Table.Td align="center">
                    <Tooltip label="View Details">
                      <ActionIcon color="blue" variant="light" onClick={() => handleViewDetails(ticket)}>
                        <IconEye size={18} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              ))}
              {tickets.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={6} align="center">No tickets found.</Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Paper>

      {/* View Details Modal */}
      <Modal opened={viewOpened} onClose={closeView} title="Ticket Details" centered size="lg">
        {selectedTicket && (
          <Box>
            <Text size="sm" c="dimmed">Ticket ID: <Text span fw={700} c="dark">{selectedTicket.ticketId}</Text></Text>
            
            <Group justify="space-between" mt="xs">
                <Box>
                    <Text size="sm" c="dimmed">Subject</Text>
                    <Title order={4}>{selectedTicket.subject}</Title>
                </Box>
                <Badge size="lg" color={getStatusColor(selectedTicket.status)}>
                    {selectedTicket.status.replace('_', ' ').toUpperCase()}
                </Badge>
            </Group>

            {/* Display Specific Details based on Type */}
            {(selectedTicket.type === 'complaint' || selectedTicket.type === 'incident_report') && (
                <Paper withBorder p="sm" bg="gray.0" mt="md">
                    <Text size="sm" fw={700} mb="xs">Incident Details</Text>
                    <Group gap="xl">
                        <Box>
                            <Text size="xs" c="dimmed">Date</Text>
                            <Text size="sm">{selectedTicket.incidentDate ? new Date(selectedTicket.incidentDate).toLocaleDateString() : 'N/A'}</Text>
                        </Box>
                        <Box>
                            <Text size="xs" c="dimmed">Location</Text>
                            <Text size="sm">{selectedTicket.incidentLocation || 'N/A'}</Text>
                        </Box>
                        <Box>
                            <Text size="xs" c="dimmed">Involved</Text>
                            <Text size="sm">{selectedTicket.personsInvolved || 'N/A'}</Text>
                        </Box>
                    </Group>
                </Paper>
            )}

            {selectedTicket.type === 'service_request' && (
                <Paper withBorder p="sm" bg="blue.0" mt="md">
                    <Text size="sm" fw={700} mb="xs">Request Details</Text>
                    <Group gap="xl">
                        <Box>
                            <Text size="xs" c="dimmed">Service Type</Text>
                            <Text size="sm">{selectedTicket.serviceType || 'N/A'}</Text>
                        </Box>
                        <Box>
                            <Text size="xs" c="dimmed">Preferred Date</Text>
                            <Text size="sm">{selectedTicket.preferredDate ? new Date(selectedTicket.preferredDate).toLocaleDateString() : 'N/A'}</Text>
                        </Box>
                    </Group>
                </Paper>
            )}
            
            <Text size="sm" c="dimmed" mt="md">Description</Text>
            <Paper bg="gray.0" p="md" radius="md" withBorder>
              <Text style={{ whiteSpace: 'pre-wrap' }}>
                {selectedTicket.description}
              </Text>
            </Paper>

            {selectedTicket.proofImages && selectedTicket.proofImages.length > 0 && (
                <>
                    <Text size="sm" c="dimmed" mt="md">Attachments</Text>
                    <Group>
                        {selectedTicket.proofImages.map((img, i) => (
                            <Image 
                                key={i} 
                                src={img} 
                                h={100} w={100} radius="md" 
                                style={{ cursor: 'pointer', border: '1px solid #eee' }}
                                onClick={() => setImagePreview(img)}
                            />
                        ))}
                    </Group>
                </>
            )}
          </Box>
        )}
        <Group justify="flex-end" mt="md">
          <Button onClick={closeView} variant="default">Close</Button>
        </Group>
      </Modal>

      {/* Create Ticket Modal */}
      <Modal opened={opened} onClose={close} title="Log New Ticket" centered size="lg">
        <Box>
          <Select 
            label="Type"
            required
            value={formData.type} 
            onChange={(val) => handleChange('type', val)}
            mb="md"
            data={[
              { value: 'complaint', label: 'Complaint' },
              { value: 'incident_report', label: 'Incident Report' },
              { value: 'service_request', label: 'Service Request' }
            ]}
          />

          <TextInput 
            label="Subject" 
            placeholder="Brief title of the issue"
            required
            mb="md" 
            value={formData.subject} 
            onChange={(e) => handleChange('subject', e.target.value)} 
          />

          {/* DYNAMIC FIELDS: Service Request */}
          {formData.type === 'service_request' && (
              <Group grow mb="md">
                  <Select 
                    label="Service Type"
                    placeholder="Select Service"
                    required
                    data={['Plumbing', 'Electrical', 'Construction Permit', 'Grass Cutting', 'Garbage Collection', 'Other']}
                    value={formData.serviceType}
                    onChange={(val) => handleChange('serviceType', val)}
                  />
                  <DatePickerInput 
                    label="Preferred Date"
                    placeholder="When do you need this?"
                    required
                    leftSection={<IconCalendar size={16}/>}
                    minDate={new Date()}
                    value={formData.preferredDate}
                    onChange={(val) => handleChange('preferredDate', val)}
                  />
              </Group>
          )}

          {/* DYNAMIC FIELDS: Incident / Complaint */}
          {(formData.type === 'incident_report' || formData.type === 'complaint') && (
              <Stack gap="xs" mb="md">
                  <Group grow>
                    <TextInput 
                        label="Location of Incident" 
                        placeholder="e.g., Near the clubhouse, Blk 5"
                        leftSection={<IconMapPin size={16}/>}
                        required
                        value={formData.incidentLocation}
                        onChange={(e) => handleChange('incidentLocation', e.target.value)}
                    />
                    <DatePickerInput 
                        label="Date of Incident"
                        placeholder="When did this happen?"
                        maxDate={new Date()}
                        required
                        leftSection={<IconCalendar size={16}/>}
                        value={formData.incidentDate}
                        onChange={(val) => handleChange('incidentDate', val)}
                    />
                  </Group>
                  <TextInput 
                    label="Persons Involved (Optional)"
                    placeholder="Names of people involved or witnesses"
                    leftSection={<IconUsers size={16}/>}
                    value={formData.personsInvolved}
                    onChange={(e) => handleChange('personsInvolved', e.target.value)}
                  />
              </Stack>
          )}
          
          <Textarea 
            label="Full Description" 
            placeholder="Please provide as much detail as possible..."
            required
            minRows={4} 
            mb="md"
            value={formData.description} 
            onChange={(e) => handleChange('description', e.target.value)} 
          />

          <FileInput
             label="Attachments (Max 3)"
             placeholder="Upload photos or documents"
             multiple
             accept="image/*"
             leftSection={<IconUpload size={16} />}
             value={selectedFiles}
             onChange={handleFileChange}
          />
        </Box>
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={close}>Cancel</Button>
          <Button color="orange" onClick={handleSubmit} leftSection={<IconMessageReport size={18} />}>
            Submit Ticket
          </Button>
        </Group>
      </Modal>

      {/* Image Preview */}
      <Modal 
        opened={!!imagePreview} 
        onClose={() => setImagePreview(null)} 
        size="xl"
        withCloseButton={false}
        styles={{ body: { padding: 0 }, content: { backgroundColor: 'transparent', boxShadow: 'none' } }}
        centered
      >
        <Box pos="relative">
            <ActionIcon 
                onClick={() => setImagePreview(null)} 
                color="gray"
                variant="filled"
                radius="xl"
                style={{ position: 'absolute', right: 10, top: 10, zIndex: 10 }}
            >
                <IconX size={20} />
            </ActionIcon>
            <Image src={imagePreview} radius="md" style={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
        </Box>
      </Modal>
    </Layout>
  );
};

export default ResidentTickets;